import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import marketplaceService from '../../../services/marketplaceService';

const MyListings = ({ onRefresh }) => {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadListings();
  }, [filter]);

  const loadListings = async () => {
    setLoading(true);
    const statusFilter = filter === 'all' ? null : filter;
    const { data, error } = await marketplaceService?.getUserListings(user?.id, statusFilter);
    if (!error) {
      setListings(data || []);
    }
    setLoading(false);
  };

  const handleCancelListing = async (listingId) => {
    if (!confirm('Are you sure you want to cancel this listing?')) return;
    
    const { error } = await marketplaceService?.cancelListing(listingId);
    if (!error) {
      loadListings();
      onRefresh?.();
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      sold: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles?.[status] || styles?.active}`}>
        {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="bg-card border border-border rounded-lg h-32" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter Tabs */}
      <div className="flex space-x-4">
        {['all', 'active', 'pending', 'sold', 'cancelled']?.map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              filter === status
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
          </button>
        ))}
      </div>
      {/* Listings */}
      {listings?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No listings found</h3>
          <p className="text-muted-foreground">Create your first listing to start selling</p>
        </div>
      ) : (
        <div className="space-y-4">
          {listings?.map(listing => (
            <div key={listing?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start space-x-4">
                {/* Image */}
                <div className="w-24 h-24 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
                  {listing?.featured_image_url || listing?.collectible?.primary_image_url ? (
                    <img
                      src={listing?.featured_image_url || listing?.collectible?.primary_image_url}
                      alt={listing?.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Image" size={32} className="text-muted-foreground" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">{listing?.title}</h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">{listing?.description}</p>
                    </div>
                    {getStatusBadge(listing?.status)}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Price</p>
                      <p className="text-lg font-bold text-primary">${parseFloat(listing?.asking_price || 0)?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Views</p>
                      <p className="text-lg font-semibold text-foreground">{listing?.view_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Offers</p>
                      <p className="text-lg font-semibold text-foreground">{listing?.offers?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Condition</p>
                      <p className="text-sm font-medium text-foreground capitalize">
                        {listing?.condition?.replace('-', ' ')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {listing?.status === 'active' && (
                  <div className="flex flex-col space-y-2">
                    <Button
                      size="sm"
                      variant="outline"
                      iconName="Trash2"
                      onClick={() => handleCancelListing(listing?.id)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyListings;
